// src/pages/Login.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';

// Collapse the two-panel layout to a single column on phones/tablets.
function useIsMobile(breakpoint = 820) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// Only allow same-origin internal paths as a post-login return target.
function safeNext(n) {
  return n && n.startsWith('/') && !n.startsWith('//') ? n : null;
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    background: 'var(--ka-navy)',
    position: 'relative',
    overflow: 'hidden',
  },
  bgPattern: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `
      radial-gradient(ellipse at 20% 50%, rgba(200,151,58,0.12) 0%, transparent 60%),
      radial-gradient(ellipse at 80% 20%, rgba(200,151,58,0.08) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
  },
  bgLines: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 40px,
      rgba(255,255,255,0.015) 40px,
      rgba(255,255,255,0.015) 41px
    )`,
    pointerEvents: 'none',
  },
  left: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '60px 80px',
    position: 'relative',
    zIndex: 2,
  },
  kaBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 14,
    marginBottom: 40,
  },
  kaCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: 'var(--ka-gold)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    fontWeight: 800,
    fontFamily: 'var(--font-display)',
    color: 'var(--ka-navy)',
    boxShadow: '0 4px 24px rgba(200,151,58,0.5)',
  },
  kaName: {
    fontFamily: 'var(--font-display)',
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--ka-white)',
    lineHeight: 1.2,
  },
  kaSub: {
    fontSize: 11,
    color: 'var(--ka-gold-light)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    marginTop: 2,
  },
  headline: {
    fontFamily: 'var(--font-display)',
    fontSize: 52,
    fontWeight: 700,
    color: 'var(--ka-white)',
    lineHeight: 1.1,
    marginBottom: 20,
  },
  headlineGold: { color: 'var(--ka-gold)' },
  tagline: {
    fontSize: 17,
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 1.7,
    maxWidth: 460,
    fontWeight: 300,
  },
  dividerLine: {
    width: 60,
    height: 3,
    background: 'var(--ka-gold)',
    borderRadius: 2,
    margin: '28px 0',
  },
  right: {
    width: 460,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    position: 'relative',
    zIndex: 2,
  },
  card: {
    background: 'var(--ka-white)',
    borderRadius: 16,
    padding: '40px 36px',
    width: '100%',
    boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
  },
  cardTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--ka-navy)',
    marginBottom: 4,
  },
  cardSub: {
    fontSize: 13,
    color: 'var(--ka-gray-600)',
    marginBottom: 24,
    lineHeight: 1.5,
  },
  tabRow: {
    display: 'flex',
    background: 'var(--ka-gray-100)',
    borderRadius: 8,
    padding: 4,
    marginBottom: 20,
    gap: 4,
  },
  tab: {
    flex: 1,
    padding: '8px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'center',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    color: 'var(--ka-gray-400)',
    transition: 'all 0.15s',
    fontFamily: 'var(--font-body)',
  },
  tabActive: {
    background: 'var(--ka-white)',
    color: 'var(--ka-navy)',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  },
  googleBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '13px 20px',
    background: 'var(--ka-navy)',
    color: 'var(--ka-white)',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.2s',
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--ka-gray-600)',
    marginBottom: 6,
    marginTop: 14,
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    border: '1.5px solid var(--ka-gray-200)',
    borderRadius: 7,
    fontSize: 16, // 16px avoids iOS auto-zoom on focus
    color: 'var(--ka-navy)',
    fontFamily: 'var(--font-body)',
    outline: 'none',
    background: 'var(--ka-white)',
    boxSizing: 'border-box',
  },
  submitBtn: {
    width: '100%',
    padding: '13px',
    marginTop: 20,
    background: 'var(--ka-gold)',
    color: 'var(--ka-navy)',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 7,
    padding: '10px 14px',
    fontSize: 13,
    color: '#b91c1c',
    marginTop: 12,
  },
  blockBox: {
    background: '#fff7ed',
    border: '1.5px solid #fdba74',
    borderRadius: 7,
    padding: '12px 14px',
    fontSize: 13,
    color: '#9a3412',
    marginTop: 12,
    lineHeight: 1.5,
  },
  notice: {
    background: 'var(--ka-gold-pale)',
    border: '1px solid rgba(200,151,58,0.3)',
    borderRadius: 8,
    padding: '12px 14px',
    fontSize: 12,
    color: 'var(--ka-navy)',
    lineHeight: 1.5,
    marginTop: 16,
  },
  devBadge: {
    display: 'inline-block',
    background: '#fef3c7',
    color: '#92400e',
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 4,
    marginLeft: 8,
    verticalAlign: 'middle',
  },
  footer: {
    marginTop: 20,
    fontSize: 11,
    color: 'var(--ka-gray-400)',
    textAlign: 'center',
    lineHeight: 1.6,
  },
};

export default function Login() {
  const {
    signInWithGoogle,
    blockReason,
    clearBlockReason,
    user,
    isStudent,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // Where to send the user after a successful sign-in, if a caller asked for a
  // specific return path (e.g. the trip-join page): /login?next=/trip-join?s=…
  const nextUrl = safeNext(new URLSearchParams(location.search).get('next'));

  const [tab, setTab] = useState('google');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hovering, setHovering] = useState(false);

  // If we're already signed in (and not blocked), navigate away. Honors a
  // ?next= return target first; otherwise students go to their student-only
  // view and faculty/staff to the home dashboard.
  useEffect(() => {
    if (user && !blockReason) {
      navigate(nextUrl || (isStudent ? '/my-checkout' : '/'));
    }
  }, [user, blockReason, isStudent, navigate, nextUrl]);

  useEffect(() => {
    if (blockReason) {
      setLoading(false);
    }
  }, [blockReason]);

  const handleGoogle = async () => {
    setError('');
    clearBlockReason();
    setLoading(true);
    try {
      await signInWithGoogle();
      // The useEffect above navigates once user is confirmed and unblocked.
    } catch (err) {
      setError(err.code === 'auth/popup-closed-by-user'
        ? 'Sign-in cancelled. Please try again.'
        : 'Google sign-in failed. Try again or use email below.');
      setLoading(false);
    }
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    setError('');
    clearBlockReason();
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const msgs = {
        'auth/user-not-found': 'No account found with that email.',
        'auth/wrong-password': 'Incorrect password.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/invalid-credential': 'Incorrect email or password.',
      };
      setError(msgs[err.code] || err.message || 'Something went wrong.');
      setLoading(false);
    }
  };

  const tabStyle = (id) => ({
    ...styles.tab,
    ...(tab === id ? styles.tabActive : {}),
  });

  // --- Responsive style variants -------------------------------------------
  const page = {
    ...styles.page,
    ...(isMobile ? { flexDirection: 'column', overflow: 'auto' } : {}),
  };
  const left = {
    ...styles.left,
    ...(isMobile
      ? { flex: 'none', padding: '40px 24px 8px', justifyContent: 'flex-start' }
      : {}),
  };
  const headline = {
    ...styles.headline,
    ...(isMobile ? { fontSize: 34, marginBottom: 12 } : {}),
  };
  const tagline = {
    ...styles.tagline,
    ...(isMobile ? { fontSize: 14, maxWidth: 'none' } : {}),
  };
  const right = {
    ...styles.right,
    ...(isMobile ? { width: '100%', padding: '16px 16px 40px' } : {}),
  };
  const card = {
    ...styles.card,
    ...(isMobile
      ? { padding: '28px 20px', maxWidth: 460, margin: '0 auto' }
      : {}),
  };

  return (
    <div style={page}>
      <div style={styles.bgPattern} />
      <div style={styles.bgLines} />

      {/* Left panel */}
      <div style={left}>
        <div style={styles.kaBadge}>
          <div style={styles.kaCircle}>KA</div>
          <div>
            <div style={styles.kaName}>King's Academy</div>
            <div style={styles.kaSub}>Madaba, Jordan</div>
          </div>
        </div>
        {!isMobile && (
          <>
            <div style={headline}>
              Classroom<br /><span style={styles.headlineGold}>Tool Kit</span>
            </div>
            <div style={styles.dividerLine} />
            <p style={tagline}>
              One place for every tool, resource, and workflow
              the King's Academy community relies on every day.
            </p>
          </>
        )}
      </div>

      {/* Right card */}
      <div style={right}>
        <div style={card}>
          <div style={styles.cardTitle}>Welcome back</div>
          <div style={styles.cardSub}>
            {nextUrl
              ? 'Sign in with your King’s Academy account to continue.'
              : 'Sign in to access the King’s Academy Classroom Tool Kit.'}
          </div>

          {/* Tab switcher */}
          <div style={styles.tabRow}>
            <button
              style={tabStyle('google')}
              onClick={() => { setTab('google'); setError(''); clearBlockReason(); }}
            >
              Google SSO
            </button>
            <button
              style={tabStyle('email')}
              onClick={() => { setTab('email'); setError(''); clearBlockReason(); }}
            >
              Email / Password
              <span style={styles.devBadge}>Dev</span>
            </button>
          </div>

          {/* Google tab */}
          {tab === 'google' && (
            <>
              <button
                style={{
                  ...styles.googleBtn,
                  opacity: loading ? 0.7 : 1,
                  ...(hovering && !loading ? { background: 'var(--ka-navy-mid)', transform: 'translateY(-1px)' } : {}),
                }}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                onClick={handleGoogle}
                disabled={loading}
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loading ? 'Signing in…' : 'Sign in with Google'}
              </button>
              <div style={styles.notice}>
                🔒 Use your <strong>@kingsacademy.edu.jo</strong> Google account.
                This portal is primarily for faculty and staff, but students will use for end-of-year checkout.
              </div>
              {blockReason && <div style={styles.blockBox}>{blockReason}</div>}
              {error && <div style={styles.error}>{error}</div>}
            </>
          )}

          {/* Email tab — sign in only, no registration */}
          {tab === 'email' && (
            <form onSubmit={handleEmail}>
              <label style={styles.label}>Email</label>
              <input
                style={styles.input}
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />

              <label style={styles.label}>Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />

              {blockReason && <div style={styles.blockBox}>{blockReason}</div>}
              {error && <div style={styles.error}>{error}</div>}

              <button
                type="submit"
                style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
                disabled={loading}
              >
                {loading ? 'Please wait…' : 'Sign In'}
              </button>
            </form>
          )}

          <div style={styles.footer}>
            King's Academy · Madaba, Jordan<br />
            Classroom Tool Kit <span style={{ color: 'var(--ka-gold)' }}></span>
          </div>
        </div>
      </div>
    </div>
  );
}